import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { createSlider, updateSlider, getSliderById } from "../../services/api";
import { encodeFileContent } from "../../utils/fileUtils";
import { Slider } from "../../types/types";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import AdminLayout from "./AdminLayout";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName } from "../../utils/media";

const SliderForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<Slider>({
    defaultValues: {
      titrearabe: "",
      titrefrancais: "",
      textarabe: "",
      textfrancais: "",
      image: null,
      actif: true,
      ordre: 0,
      buttons: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "buttons",
  });

  useEffect(() => {
    if (isEditMode && id) {
      const fetchSlider = async () => {
        try {
          const response = await getSliderById(Number(id));
          const data = response.data;
          reset({
            ...data,
            // Map etat from API to actif for form
            actif: data.etat,
            image: data.image || null,
          });
        } catch (error) {
          console.error("Error fetching slider:", error);
          toast.error(getTranslation("fetchError", language));
        }
      };
      fetchSlider();
    }
  }, [id, isEditMode, reset]);

  const handleApiError = (error: unknown, defaultMessage: string = "حدث خطأ أثناء العملية") => {
    console.error("API Error:", error);
    
    if (error instanceof AxiosError) {
      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      console.error("Server error details:", error.response?.data);
      
      if (serverMessage) {
        toast.error(serverMessage);
      } else if (error.message) {
        toast.error(`خطأ في الاتصال: ${error.message}`);
      } else {
        toast.error(defaultMessage);
      }
    } else if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error(defaultMessage);
    }
  };

const onSubmit = async (formData: Slider) => {
  setIsLoading(true);
  try {
    let imageValue: string | null = null;

    // Handle image conversion
    if (formData.image) {
      if (formData.image instanceof FileList && formData.image.length > 0) {
        const file = formData.image[0];
        imageValue = await encodeFileContent(file);
      } else if (typeof formData.image === "string") {
        // Keep existing filename on edit mode
        imageValue = getFileName(formData.image) || formData.image;
      }
    }

    // Prepare payload according to API expectations
    const payload = {
      id: isEditMode ? formData.id : undefined,
      titrearabe: formData.titrearabe,
      titrefrancais: formData.titrefrancais,
      textarabe: formData.textarabe,
      textfrancais: formData.textfrancais,
      etat: formData.actif, // Map actif to etat for API
      ordre: Number(formData.ordre) || 0,
      image: imageValue,
      buttons: formData.buttons || [],
    };

    if (isEditMode && formData.id) {
      await updateSlider(formData.id, payload);
      toast.success("تم تحديث السلايدر بنجاح");
    } else {
      await createSlider(payload);
      toast.success("تم إضافة السلايدر بنجاح");
    }

    navigate("/admin/sliders");
  } catch (error) {
    handleApiError(error, getTranslation("saveError", language));
  } finally {
    setIsLoading(false);
  }
};

  return (

       <AdminLayout>
      <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditMode ? "تعديل سلايدر" : getTranslation("addSlider", language)}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              العنوان بالعربية *
            </label>
            <input
              {...register("titrearabe", { required: "مطلوب" })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.titrearabe && (
              <span className="text-red-500 text-sm">
                {errors.titrearabe.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              العنوان بالفرنسية *
            </label>
            <input
              {...register("titrefrancais", { required: "مطلوب" })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.titrefrancais && (
              <span className="text-red-500 text-sm">
                {errors.titrefrancais.message}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              النص بالعربية *
            </label>
            <textarea
              {...register("textarabe", { required: "مطلوب" })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.textarabe && (
              <span className="text-red-500 text-sm">
                {errors.textarabe.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              النص بالفرنسية *
            </label>
            <textarea
              {...register("textfrancais", { required: "مطلوب" })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.textfrancais && (
              <span className="text-red-500 text-sm">
                {errors.textfrancais.message}
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الصورة
          </label>
          <input
            type="file"
            accept="image/*"
            {...register("image")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            {...register("actif")}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900">مفعل</label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            الترتيب
          </label>
          <input
            type="number"
            {...register("ordre")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">الأزرار</h3>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="border p-4 rounded-md mb-4 bg-gray-50"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    عنوان بالعربية *
                  </label>
                  <input
                    placeholder="عنوان بالعربية"
                    {...register(`buttons.${index}.titrearabe` as const, {
                      required: "مطلوب",
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.buttons?.[index]?.titrearabe && (
                    <span className="text-red-500 text-sm">
                      {errors.buttons[index]?.titrearabe?.message}
                    </span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre en français *
                  </label>
                  <input
                    placeholder="Titre en français"
                    {...register(`buttons.${index}.titrefrancais` as const, {
                      required: "مطلوب",
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.buttons?.[index]?.titrefrancais && (
                    <span className="text-red-500 text-sm">
                      {errors.buttons[index]?.titrefrancais?.message}
                    </span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lien *
                  </label>
                  <input
                    placeholder="Lien"
                    {...register(`buttons.${index}.lien` as const, {
                      required: "مطلوب",
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {errors.buttons?.[index]?.lien && (
                    <span className="text-red-500 text-sm">
                      {errors.buttons[index]?.lien?.message}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(index)}
                className="bg-red-500 text-white px-3 py-1 rounded-md text-sm hover:bg-red-600"
              >
                حذف
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              append({ titrearabe: "", titrefrancais: "", lien: "" })
            }
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
          >
            إضافة زر
          </button>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/admin/sliders")}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {isLoading ? getTranslation("saving", language) : "حفظ"}
          </button>
        </div>
      </form>
    </div>
    </AdminLayout>
  );
};

export default SliderForm;